<template>
  <div class="reporte-page">
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="reporte-shell">
      <!-- Header -->
      <header class="page-header fade-in">
        <div class="page-icon-box">
          <v-icon color="#F2F2ED" size="20">mdi-chart-line</v-icon>
        </div>
        <div>
          <h1 class="page-title">Reporte de ventas</h1>
          <p class="page-sub">Análisis de productos vendidos</p>
        </div>
      </header>

      <!-- Filtros de período -->
      <section class="fade-in delay-100">
        <div class="periodo-wrapper">
          <periodo-busqueda @buscar="onBuscar" />
        </div>
      </section>

      <!-- Totales por producto -->
      <section v-if="ventas.length > 0" class="fade-in delay-150 mt-lg">
        <div class="totales-wrapper">
          <cartas-totales
            :totales="totalesProductos"
            :titulo="'Pagos realizados por productos'"
            :icono="'mdi-cash-multiple'"
            :color="'secondary'"
          />
        </div>
      </section>

      <!-- Contenido principal -->
      <section class="fade-in delay-200 mt-lg">
        <div class="card-flat card-flat-nopad">
          <!-- Resumen -->
          <div class="summary-head">
            <div>
              <span class="summary-label">Total de ventas</span>
              <div class="summary-value">${{ formatMoney(totalVentas) }}</div>
            </div>
            <div class="summary-period">
              <v-icon size="14" class="mr-2" color="#5C5C62">mdi-calendar-range</v-icon>
              <span v-if="!filtros.fechaInicio">Hoy</span>
              <span v-else>{{ filtros.fechaInicio }} – {{ filtros.fechaFin }}</span>
            </div>
          </div>

          <!-- Acciones y búsqueda -->
          <div class="table-controls">
            <div class="search-input-wrapper">
              <v-icon size="18" color="#5C5C62">mdi-magnify</v-icon>
              <input v-model="busqueda" type="text" class="search-input" placeholder="Buscar en la tabla…" />
            </div>

            <div class="export-buttons">
              <button class="btn-ghost" @click="generarPDF">
                <v-icon size="15" class="mr-2">mdi-file-pdf-box</v-icon>
                PDF
              </button>
              <button class="btn-primary" @click="generarExcel">
                <v-icon size="15" class="mr-2">mdi-file-excel</v-icon>
                Excel
              </button>
            </div>
          </div>

          <!-- Tabla -->
          <v-data-table
            :loading="cargando"
            :headers="encabezadoTabla"
            :items="ventas"
            :search="busqueda"
            sort-by="producto"
            no-data-text="No hay ventas registradas"
            no-results-text="No se encontraron coincidencias"
            class="dash-table"
            :footer-props="{ itemsPerPageText: 'Por página' }"
          >
            <template v-slot:[`item.imagen`]="{ item }">
              <img v-if="item.imagen" :src="urlImagen(item.imagen)" alt="Producto" class="product-image" />
              <div v-else class="product-image-placeholder">
                <v-icon size="14" color="#5C5C62">mdi-image-off</v-icon>
              </div>
            </template>

            <template v-slot:[`item.nombre_producto`]="{ item }">
              <span class="cell-strong">{{ item.nombre_producto }}</span>
            </template>

            <template v-slot:[`item.cantidad`]="{ item }">
              <span class="status-tag status-tag-neutral">{{ item.cantidad }}</span>
            </template>

            <template v-slot:[`item.subtotal`]="{ item }">
              <span class="cell-price">${{ formatMoney(item.subtotal) }}</span>
            </template>

            <template v-slot:[`item.fecha`]="{ item }">
              <span class="cell-secondary">{{ formatearFecha(item.fecha) }}</span>
            </template>

            <template slot="no-data">
              <div class="empty-state">
                <div class="empty-icon">
                  <v-icon size="28" color="#5C5C62">mdi-chart-line-variant</v-icon>
                </div>
                <h3 class="empty-title">No hay ventas registradas</h3>
                <p class="empty-desc">No se encontraron ventas para el período seleccionado</p>
              </div>
            </template>

            <template v-slot:loading>
              <div class="loading-state">
                <div class="spinner"></div>
                <p class="loading-text">Cargando datos…</p>
              </div>
            </template>
          </v-data-table>
        </div>
      </section>
    </div>

    <!-- Loading Overlay -->
    <div v-if="cargando && ventas.length === 0" class="loading-overlay">
      <div class="loading-inner">
        <div class="spinner"></div>
        <p class="loading-text">Cargando reporte de ventas…</p>
      </div>
    </div>
  </div>
</template>

<script>
import HttpService from '../../Servicios/HttpService'
import Utiles from '../../Servicios/Utiles'
import PeriodoBusqueda from '../Dialogos/PeriodoBusqueda.vue'
import CartasTotales from '../Dialogos/CartasTotales.vue'
import { saveAs } from 'file-saver'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable'

export default {
  name: "Ventas",
  components: { PeriodoBusqueda, CartasTotales },

  data() {
    return {
      busqueda: "",
      cargando: false,
      ventas: [],
      encabezadoTabla: [
        { text: "Producto", sortable: true, value: "nombre_producto" },
        { text: "Cantidad", sortable: true, value: "cantidad" },
        { text: "Monto pagado", sortable: true, value: "subtotal" },
        { text: "Fecha", sortable: true, value: "fecha" },
      ],
      filtros: {
        fechaInicio: null,
        fechaFin: null
      },
      totalVentas: 0,
      totalesProductos: [],
      totalesVendedores: []
    }
  },

  mounted() {
    this.obtenerVentas()
  },

  methods: {
    formatMoney(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toLocaleString('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatearFecha(fechaString) {
      if (!fechaString) return 'Sin fecha';

      try {
        const fecha = new Date(fechaString);
        if (isNaN(fecha.getTime())) return 'Fecha inválida';

        const options = {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        };
        return fecha.toLocaleDateString('es-ES', options);
      } catch (e) {
        return 'Fecha inválida';
      }
    },

    generarExcel() {
      try {
        const datos = this.ordenarVentas('fecha', false).map(venta => ({
          Producto: venta.nombre_producto,
          Cantidad: venta.cantidad,
          'Monto pagado': venta.subtotal,
          Fecha: venta.fecha,
        }));

        const ws = XLSX.utils.json_to_sheet(datos, { origin: 'A3' });

        // Título
        XLSX.utils.sheet_add_aoa(ws, [["Reporte de Ventas Productos"], [],], { origin: 'A1' });

        // Filtros automáticos
        ws['!autofilter'] = { ref: `A3:D${datos.length + 3}` };

        // Anchos de columna
        ws['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 20 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Ventas');

        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 'reporte_ventas_productos.xlsx');

        // Notificación de éxito
        this.$store.commit('mostrarMensaje', {
          texto: '✅ Excel generado correctamente',
          color: 'success'
        });
      } catch (error) {
        console.error('Error generando Excel:', error);
        this.$store.commit('mostrarMensaje', {
          texto: '❌ Error al generar el Excel',
          color: 'error'
        });
      }
    },

    generarPDF() {
      try {
        const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'A4' });

        const pageWidth = doc.internal.pageSize.getWidth();
        const title = "Reporte de Ventas Productos";
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        const textWidth = doc.getTextWidth(title);
        doc.text(title, (pageWidth - textWidth) / 2, 40);

        // Subtítulo con período
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        let subtitle = 'Período: ';
        if (this.filtros.fechaInicio) {
          subtitle += `${this.filtros.fechaInicio} - ${this.filtros.fechaFin}`;
        } else {
          subtitle += 'Hoy';
        }
        const subtitleWidth = doc.getTextWidth(subtitle);
        doc.text(subtitle, (pageWidth - subtitleWidth) / 2, 60);

        const columnas = [
          { header: 'Producto', dataKey: 'nombre_producto' },
          { header: 'Cantidad', dataKey: 'cantidad' },
          { header: 'Monto pagado', dataKey: 'total' },
          { header: 'Fecha', dataKey: 'fecha' },
        ];

        autoTable(doc, {
          startY: 80,
          head: [columnas.map(col => col.header)],
          body: this.ventas.map(item => [
            item.nombre_producto,
            item.cantidad,
            `$${this.formatMoney(item.subtotal)}`,
            this.formatearFecha(item.fecha)
          ]),
          theme: 'striped',
          headStyles: {
            fillColor: [200, 255, 77],
            textColor: [14, 14, 16],
            fontStyle: 'bold',
          },
          styles: {
            fontSize: 10,
            cellPadding: 5,
          },
          margin: { top: 80, bottom: 50 },
        });

        // Total al final
        const finalY = doc.lastAutoTable.finalY + 20;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`Total de Ventas: $${this.formatMoney(this.totalVentas)}`, 40, finalY);

        doc.save('reporte_ventas_productos.pdf');

        // Notificación de éxito
        this.$store.commit('mostrarMensaje', {
          texto: '✅ PDF generado correctamente',
          color: 'success'
        });
      } catch (error) {
        console.error('Error generando PDF:', error);
        this.$store.commit('mostrarMensaje', {
          texto: '❌ Error al generar el PDF',
          color: 'error'
        });
      }
    },

    ordenarVentas(campo, asc = true) {
      return [...this.ventas].sort((a, b) => {
        if (a[campo] < b[campo]) return asc ? -1 : 1;
        if (a[campo] > b[campo]) return asc ? 1 : -1;
        return 0;
      });
    },

    onBuscar(fechas) {
      console.log(fechas);
      this.filtros = {
        fechaInicio: fechas[0],
        fechaFin: fechas[1]
      };
      this.obtenerVentas();
    },

    urlImagen(imagen) {
      return Utiles.generarURL(imagen);
    },

    async obtenerVentas() {
      this.cargando = true;
      let payload = {
        metodo: 'obtener',
        filtros: this.filtros
      };

      try {
        const respuesta = await HttpService.obtenerConDatos(payload, 'ventas.php');

        this.ventas = respuesta.ventas;
        this.totalVentas = respuesta.totalVentas;
        this.totalesProductos = respuesta.totalesProductos;

        console.log(respuesta);
      } catch (err) {
        console.error("Error al obtener las ventas", err);
        this.$store.commit('mostrarMensaje', {
          texto: '❌ Error al cargar las ventas',
          color: 'error'
        });
      } finally {
        this.cargando = false;
      }
    },
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');

/* ===== TOKENS ===== */
.reporte-page {
  --bg: #0E0E10;
  --card: #17171A;
  --card-border: #26262A;
  --text-primary: #F2F2ED;
  --text-secondary: #8C8C93;
  --text-tertiary: #5C5C62;
  --accent: #C8FF4D;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --radius: 4px;

  --font-display: 'Space Grotesk', sans-serif;
  --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', monospace;
}

.reporte-page {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  position: relative;
  padding: 40px;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(242, 242, 237, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(242, 242, 237, 0.02) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.reporte-shell {
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
}

/* ===== HEADER ===== */
.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 22px 26px;
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  margin-bottom: 20px;
}

.page-icon-box {
  width: 42px;
  height: 42px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 2px;
}

.page-sub {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

/* Envolturas neutras para componentes externos (PeriodoBusqueda,
   CartasTotales) — les damos el marco del sistema por fuera, ya que no
   controlamos sus estilos internos desde aquí. */
.periodo-wrapper,
.totales-wrapper {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 18px 22px;
}

/* ===== CARD / RESUMEN ===== */
.card-flat {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.card-flat-nopad {
  overflow: hidden;
}

.summary-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 22px 24px;
  border-bottom: 1px solid var(--card-border);
}

.summary-label {
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}

.summary-value {
  font-family: var(--font-display);
  font-size: 28px;
  font-weight: 700;
  color: var(--accent);
  margin-top: 4px;
}

.summary-period {
  display: flex;
  align-items: center;
  font-size: 12.5px;
  color: var(--text-secondary);
}

/* ===== CONTROLS ===== */
.table-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 18px 24px;
  border-bottom: 1px solid var(--card-border);
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 0 14px;
  flex: 1;
  min-width: 240px;
  max-width: 420px;
  transition: border-color 0.15s ease;
}

.search-input-wrapper:focus-within {
  border-color: var(--accent);
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 11px 0;
  color: var(--text-primary);
  font-size: 13.5px;
  font-family: var(--font-body);
}

.search-input::placeholder {
  color: var(--text-tertiary);
}

.export-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

/* ===== BUTTONS ===== */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-primary:hover {
  filter: brightness(1.08);
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.btn-ghost:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

/* ===== TABLE CELLS ===== */
.product-image {
  width: 32px;
  height: 32px;
  border-radius: var(--radius);
  object-fit: cover;
  border: 1px solid var(--card-border);
}

.product-image-placeholder {
  width: 32px;
  height: 32px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
}

.cell-strong {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.cell-secondary {
  font-size: 12.5px;
  color: var(--text-secondary);
}

.cell-price {
  font-size: 13px;
  font-weight: 600;
  color: var(--accent);
}

.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: var(--radius);
  font-size: 10.5px;
  font-weight: 600;
  font-family: var(--font-mono);
}

.status-tag-neutral {
  background: rgba(140, 140, 147, 0.14);
  color: var(--text-secondary);
}

/* ===== EMPTY / LOADING ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 56px 20px;
}

.empty-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--radius);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-title {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 6px;
}

.empty-desc {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 0;
  gap: 16px;
}

.loading-text {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-secondary);
}

.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(14, 14, 16, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}

.loading-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 22px;
  height: 22px;
  border: 2px solid var(--card-border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ===== MOTION ===== */
.fade-in {
  animation: fadeIn 0.4s ease both;
}

.delay-100 { animation-delay: 0.05s; }
.delay-150 { animation-delay: 0.08s; }
.delay-200 { animation-delay: 0.1s; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in, .spinner { animation: none; }
}

/* ===== Overrides v-data-table (Vuetify) ===== */
.dash-table {
  background: transparent !important;
}

.dash-table >>> .v-data-table__wrapper {
  background: transparent;
}

.dash-table >>> table {
  background: transparent;
}

.dash-table >>> thead th {
  background: rgba(255, 255, 255, 0.02) !important;
  color: var(--text-tertiary) !important;
  text-transform: uppercase;
  font-size: 10px !important;
  letter-spacing: 0.08em;
  font-family: var(--font-mono);
  border-bottom: 1px solid var(--card-border) !important;
  padding: 0 24px !important;
}

.dash-table >>> tbody td {
  border-bottom: 1px solid var(--card-border) !important;
  padding: 10px 24px !important;
}

.dash-table >>> tbody tr:hover {
  background: rgba(255, 255, 255, 0.02) !important;
}

.dash-table >>> .v-data-footer {
  color: var(--text-tertiary) !important;
  border-top: 1px solid var(--card-border) !important;
  font-size: 12px;
  padding: 0 24px;
}

.dash-table >>> .v-data-footer__select .v-select__selection {
  color: var(--text-secondary) !important;
}

.dash-table >>> .v-data-table__progress {
  background: var(--accent) !important;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .reporte-page {
    padding: 20px;
  }

  .page-header {
    padding: 18px;
  }

  .table-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input-wrapper {
    max-width: none;
  }

  .export-buttons .btn-primary,
  .export-buttons .btn-ghost {
    flex: 1;
  }

  .dash-table >>> thead th,
  .dash-table >>> tbody td {
    padding: 10px 14px !important;
    font-size: 12px !important;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Productos/ReporteVentas.vue